import type { GetStaticPaths, GetStaticProps } from 'next'

/* blocks */
import Layout from '@/cmp/templates/Layout/Layout'
import UseCase from '@/cmp/templates/use-case/UseCase'

/* utils */
import {
  getStrapiGraphqlData,
  getWordpressGraphqlData
} from '@/utils/api/wrapper'
import {
  useCaseDetailsQuery,
  UseCasePageAttributes,
  UseCasePageResponse
} from '@/utils/queries/use_case'
import { featuredCaseStudyPostQuery } from '@/utils/queries/service_category'
import {
  INSTANT_REVALIDATION,
  TEN_MINS_REVALIDATION
} from '@/utils/constants/revalidation'
import { constructMetadataFromStrapiData } from '@/utils/functions/strapi'
import { capture404PageError } from '@/utils/functions/elk-server'

interface Props {
  page: UseCasePageAttributes
  featuredCaseStudy: CaseStudy[]
}

function UseCasePage({ page, featuredCaseStudy }: Props): JSX.Element {
  return (
    <Layout
      title={page.seo.title}
      meta={constructMetadataFromStrapiData(page.seo)}
      canonical={page.seo.canonical ?? ''}
    >
      <UseCase data={page} caseStudy={featuredCaseStudy} />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug?.toString() as string

  const result = await getStrapiGraphqlData<UseCasePageResponse>(
    useCaseDetailsQuery.loc?.source.body,
    { slug }
  )

  const featuredCaseStudyResult = await getWordpressGraphqlData<{
    featuredCaseStudy: { nodes: CaseStudy[] }
  }>(featuredCaseStudyPostQuery.loc?.source.body)

  if (!result || result.page.data.length === 0) {
    capture404PageError(`/use-case/${slug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  return {
    props: {
      page: result.page.data[0].attributes,
      featuredCaseStudy: featuredCaseStudyResult?.featuredCaseStudy.nodes ?? []
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export default UseCasePage
